(() => {
  const ELEMENT_NAME = 'aa-shop-the-look';

  if (customElements.get(ELEMENT_NAME)) return;

  class AaShopTheLook extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.isSubmitting = false;
      this.abortController = new AbortController();
      this.items = [];
      this.mainTouchedOptions = new Set();
      this.mainSelectionIsExplicit = false;

      this.addButton = this.querySelector('[data-aa-add-look]');
      this.addButtonText = this.querySelector('[data-aa-add-button-text]');
      this.totalElement = this.querySelector('[data-aa-total]');
      this.validationElement = this.querySelector('[data-aa-validation]');
      this.errorElement = this.querySelector('[data-aa-error]');
      this.successElement = this.querySelector('[data-aa-success]');

      if (!this.addButton || !this.totalElement || !this.validationElement) return;

      this.createMoneyFormatter();
      this.initializeItems();
      this.connectMainProductPicker();

      this.addButton.addEventListener('click', () => this.addSelectedItems(), {
        signal: this.abortController.signal,
      });

      this.renderSummary();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.optionSelectionUnsubscriber?.();
      this.variantChangeUnsubscriber?.();
      this.initialized = false;
    }

    createMoneyFormatter() {
      const locale = this.dataset.aaLocale || document.documentElement.lang || undefined;
      const currency = this.dataset.aaCurrency;

      try {
        this.moneyFormatter = new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
        });
      } catch (_error) {
        const numberFormatter = new Intl.NumberFormat(locale);
        this.moneyFormatter = {
          format: (amount) => `${currency || ''} ${numberFormatter.format(amount)}`.trim(),
        };
      }
    }

    initializeItems() {
      this.querySelectorAll('[data-aa-stl-item]').forEach((root) => {
        const dataElement = root.querySelector('[data-aa-product-data]');
        const toggle = root.querySelector('[data-aa-piece-toggle]');
        const status = root.querySelector('[data-aa-item-status]');

        if (!dataElement || !toggle || !status) return;

        let data;
        try {
          data = JSON.parse(dataElement.textContent);
        } catch (_error) {
          toggle.checked = false;
          toggle.disabled = true;
          status.textContent = this.soldOutCopy;
          return;
        }

        const item = this.normalizeItem(root, toggle, status, data);
        if (!item) return;

        this.items.push(item);

        toggle.addEventListener(
          'change',
          () => {
            this.clearMessages();
            this.updateItemPresentation(item);
            this.renderSummary();
          },
          { signal: this.abortController.signal }
        );

        if (item.isMain) {
          this.initializeMainItem(item);
        } else {
          this.initializeRelatedItem(item);
        }
      });
    }

    normalizeItem(root, toggle, status, data) {
      if (!data || !Array.isArray(data.options) || !Array.isArray(data.variants)) return null;

      const options = data.options.map((option, index) => ({
        name: String(option.name || `Opción ${index + 1}`),
        position: Number(option.position) || index + 1,
        values: [...new Set((option.values || []).map(String))],
      }));

      const variants = data.variants
        .map((variant) => ({
          id: String(variant.id || ''),
          available: variant.available === true,
          price: Number(variant.price),
          options: Array.isArray(variant.options) ? variant.options.map(String) : [],
        }))
        .filter(
          (variant) =>
            variant.id &&
            Number.isFinite(variant.price) &&
            variant.options.length === options.length
        );

      if (!variants.length) return null;

      const hasAvailableVariant = variants.some((variant) => variant.available);
      if (!hasAvailableVariant) {
        toggle.checked = false;
        toggle.disabled = true;
      }

      return {
        root,
        toggle,
        status,
        data: {
          productId: String(data.productId || root.dataset.aaProductId || ''),
          title: String(data.title || ''),
          hasOnlyDefaultVariant: data.hasOnlyDefaultVariant === true,
          options,
          variants,
        },
        variantById: new Map(variants.map((variant) => [variant.id, variant])),
        selections: options.map(() => null),
        optionInputs: [],
        resolvedVariant: null,
        hasAvailableVariant,
        isMain: root.hasAttribute('data-aa-main-product'),
      };
    }

    initializeMainItem(item) {
      this.mainItem = item;
      const explicitVariantId = new URL(window.location.href).searchParams.get('variant');

      if (item.data.variants.length === 1) {
        item.resolvedVariant = item.data.variants[0];
        item.selections = [...item.resolvedVariant.options];
        this.mainSelectionIsExplicit = true;
      } else if (explicitVariantId && item.variantById.has(explicitVariantId)) {
        item.resolvedVariant = item.variantById.get(explicitVariantId);
        item.selections = [...item.resolvedVariant.options];
        this.mainSelectionIsExplicit = true;
      }

      this.updateItemPresentation(item);
    }

    initializeRelatedItem(item) {
      const picker = item.root.querySelector('[data-aa-variant-picker]');
      if (!picker) return;

      if (item.data.variants.length === 1) {
        item.resolvedVariant = item.data.variants[0];
        item.selections = [...item.resolvedVariant.options];
        picker.hidden = true;
        this.updateItemPresentation(item);
        return;
      }

      item.data.options.forEach((option, optionIndex) => {
        if (option.values.length === 1) item.selections[optionIndex] = option.values[0];

        const fieldset = document.createElement('fieldset');
        fieldset.className = 'aa-shop-the-look__option';

        const legend = document.createElement('legend');
        legend.className = 'aa-shop-the-look__option-name';
        legend.textContent = option.name;
        fieldset.appendChild(legend);

        const values = document.createElement('div');
        values.className = 'aa-shop-the-look__option-values';

        option.values.forEach((value, valueIndex) => {
          const controlId = `AaStlOption-${this.dataset.aaSectionId}-${item.data.productId}-${option.position}-${valueIndex}`;
          const input = document.createElement('input');
          input.className = 'aa-shop-the-look__option-input';
          input.type = 'radio';
          input.id = controlId;
          input.name = `aa-stl-${this.dataset.aaSectionId}-${item.data.productId}-${option.position}`;
          input.value = value;
          input.checked = item.selections[optionIndex] === value;
          input.dataset.aaOptionIndex = String(optionIndex);
          input.dataset.aaOptionValue = value;

          const label = document.createElement('label');
          label.className = 'aa-shop-the-look__option-value aa-chip';
          label.htmlFor = controlId;

          const labelText = document.createElement('span');
          labelText.textContent = value;
          label.appendChild(labelText);

          const stateText = document.createElement('span');
          stateText.className = 'aa-shop-the-look__option-state';
          stateText.dataset.aaOptionStateText = '';
          label.appendChild(stateText);

          input.addEventListener(
            'change',
            () => {
              if (!input.checked || input.disabled) return;
              item.selections[optionIndex] = value;
              this.reconcileFollowingSelections(item, optionIndex + 1);
              this.clearMessages();
              this.updateRelatedItem(item);
              this.renderSummary();
            },
            { signal: this.abortController.signal }
          );

          item.optionInputs.push({ input, label, stateText, optionIndex, value });
          values.append(input, label);
        });

        fieldset.appendChild(values);
        picker.appendChild(fieldset);
      });

      this.updateRelatedItem(item);
    }

    updateRelatedItem(item) {
      item.optionInputs.forEach((control) => {
        const state = this.getOptionState(item, control.optionIndex, control.value);
        const isUnavailable = state !== 'available';

        control.input.disabled = isUnavailable;
        control.input.checked = item.selections[control.optionIndex] === control.value;
        control.input.setAttribute('aria-disabled', String(isUnavailable));
        control.label.dataset.aaOptionState = state;

        if (state === 'sold-out') {
          control.stateText.textContent = this.soldOutCopy;
        } else if (state === 'nonexistent') {
          control.stateText.textContent = this.nonexistentCopy;
        } else {
          control.stateText.textContent = '';
        }
      });

      item.resolvedVariant = this.resolveVariant(item);
      this.updateItemPresentation(item);
    }

    getOptionState(item, optionIndex, value) {
      const matchingVariants = item.data.variants.filter((variant) => {
        if (variant.options[optionIndex] !== value) return false;

        return item.selections.every((selection, index) => {
          if (index >= optionIndex || selection === null) return true;
          return variant.options[index] === selection;
        });
      });

      if (matchingVariants.some((variant) => variant.available)) return 'available';
      return matchingVariants.length ? 'sold-out' : 'nonexistent';
    }

    reconcileFollowingSelections(item, startIndex) {
      for (let index = startIndex; index < item.selections.length; index += 1) {
        const selection = item.selections[index];
        if (selection === null) continue;
        if (this.getOptionState(item, index, selection) !== 'available') item.selections[index] = null;
      }
    }

    resolveVariant(item) {
      if (item.selections.some((selection) => selection === null)) return null;

      return (
        item.data.variants.find((variant) =>
          variant.options.every((value, index) => value === item.selections[index])
        ) || null
      );
    }

    updateItemPresentation(item) {
      const isSelected = item.toggle.checked && !item.toggle.disabled;
      item.root.classList.toggle('aa-shop-the-look__item--excluded', !isSelected);

      if (!item.hasAvailableVariant) {
        item.status.textContent = this.soldOutCopy;
        return;
      }

      if (!isSelected) {
        item.status.textContent = this.excludedCopy;
        return;
      }

      if (item.resolvedVariant?.available) {
        item.status.textContent = item.data.hasOnlyDefaultVariant
          ? this.readyCopy
          : item.resolvedVariant.options.join(' / ');
        return;
      }

      if (item.resolvedVariant && !item.resolvedVariant.available) {
        item.status.textContent = this.soldOutCopy;
        return;
      }

      if (item.isMain) {
        item.status.textContent = this.mainPendingCopy;
        return;
      }

      const missingOptions = item.data.options
        .filter((_option, index) => item.selections[index] === null)
        .map((option) => option.name);
      item.status.textContent = missingOptions.length
        ? `Selecciona ${missingOptions.join(', ')}.`
        : this.pendingCopy;
    }

    connectMainProductPicker() {
      if (!this.mainItem) return;

      this.mainProductInfo = Array.from(document.querySelectorAll('product-info[data-product-id]')).find(
        (productInfo) =>
          String(productInfo.dataset.productId) === this.mainItem.data.productId &&
          productInfo.id?.startsWith('MainProduct-')
      );

      if (!this.mainProductInfo || typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

      this.mainSectionId = String(this.mainProductInfo.dataset.section || '');
      this.requiredMainOptions = new Set(
        this.mainItem.data.options
          .filter((option) => option.values.length > 1)
          .map((option) => option.name)
      );

      this.optionSelectionUnsubscriber = subscribe(
        PUB_SUB_EVENTS.optionValueSelectionChange,
        ({ data } = {}) => this.handleMainOptionSelection(data)
      );
      this.variantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.variantChange,
        ({ data } = {}) => this.handleMainVariantChange(data)
      );
    }

    handleMainOptionSelection(data) {
      const eventTarget = data?.event?.target;
      const target = data?.target || eventTarget;
      if (!eventTarget || !target || !this.mainProductInfo?.contains(eventTarget)) return;

      let optionName = target.dataset.optionName;
      if (!optionName) {
        const group = target.closest('fieldset, .product-form__input--dropdown');
        const groups = Array.from(
          this.mainProductInfo.querySelectorAll('variant-selects fieldset, variant-selects .product-form__input--dropdown')
        );
        const index = groups.indexOf(group);
        optionName = this.mainItem.data.options[index]?.name;
      }

      if (optionName) this.mainTouchedOptions.add(optionName);
      this.mainItem.resolvedVariant = null;
      this.clearMessages();
      this.updateItemPresentation(this.mainItem);
      this.renderSummary();
    }

    handleMainVariantChange(data) {
      if (String(data?.sectionId || '') !== this.mainSectionId) return;

      const allRequiredOptionsTouched = [...this.requiredMainOptions].every((optionName) =>
        this.mainTouchedOptions.has(optionName)
      );

      if (!this.mainSelectionIsExplicit && !allRequiredOptionsTouched) {
        this.mainItem.resolvedVariant = null;
        this.updateItemPresentation(this.mainItem);
        this.renderSummary();
        return;
      }

      const variant = this.mainItem.variantById.get(String(data?.variant?.id || '')) || null;
      this.mainSelectionIsExplicit = true;
      this.mainItem.resolvedVariant = variant;
      this.mainItem.selections = variant ? [...variant.options] : this.mainItem.data.options.map(() => null);
      this.clearMessages();
      this.updateItemPresentation(this.mainItem);
      this.renderSummary();
    }

    renderSummary() {
      const selectedItems = this.items.filter((item) => item.toggle.checked && !item.toggle.disabled);
      const pendingItems = selectedItems.filter((item) => !item.resolvedVariant?.available);
      const count = selectedItems.length;
      const countLabel = `${count} ${count === 1 ? 'pieza' : 'piezas'}`;

      if (count === 0) {
        this.totalElement.textContent = countLabel;
        this.validationElement.textContent = this.emptyCopy;
        this.setButtonDisabled(true);
        return;
      }

      if (pendingItems.length) {
        this.totalElement.textContent = `${countLabel} · Subtotal pendiente`;
        this.validationElement.textContent = this.pendingCopy;
        this.setButtonDisabled(true);
        return;
      }

      const subtotal = selectedItems.reduce((total, item) => total + item.resolvedVariant.price, 0);
      this.totalElement.textContent = `${countLabel} · ${this.formatMoney(subtotal)}`;
      this.validationElement.textContent = '';
      this.setButtonDisabled(this.isSubmitting);
    }

    formatMoney(priceInMinorUnits) {
      return this.moneyFormatter.format(priceInMinorUnits / 100);
    }

    setButtonDisabled(disabled) {
      this.addButton.disabled = disabled;
      this.addButton.setAttribute('aria-disabled', String(disabled));
    }

    setLoading(loading) {
      this.isSubmitting = loading;
      this.addButton.classList.toggle('aa-shop-the-look__add-button--loading', loading);
      this.addButton.setAttribute('aria-busy', String(loading));
      this.addButtonText.textContent = loading ? this.loadingLabel : this.addLabel;
      this.setButtonDisabled(loading);
    }

    getSelectedCartItems() {
      const selectedByProduct = new Map();

      this.items.forEach((item) => {
        if (!item.toggle.checked || item.toggle.disabled || !item.resolvedVariant?.available) return;
        if (selectedByProduct.has(item.data.productId)) return;

        selectedByProduct.set(item.data.productId, {
          id: item.resolvedVariant.id,
          quantity: 1,
        });
      });

      return [...selectedByProduct.values()];
    }

    getCartRenderer() {
      return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    }

    getSectionsToRender(cart) {
      let sections = [];

      try {
        sections = cart?.getSectionsToRender?.().map((section) => section.id) || [];
      } catch (_error) {
        sections = [];
      }

      if (!cart) sections.push('cart-icon-bubble');
      return [...new Set(sections.filter(Boolean))].slice(0, 5);
    }

    async addSelectedItems() {
      if (this.isSubmitting) return;

      this.renderSummary();
      if (this.addButton.disabled) return;

      const items = this.getSelectedCartItems();
      if (!items.length) return;

      this.clearMessages();
      this.setLoading(true);

      const cart = this.getCartRenderer();
      const sections = this.getSectionsToRender(cart);
      cart?.setActiveElement?.(this.addButton);

      const beforeCart = await this.fetchCart().catch(() => null);
      const linesDeferred = this.createCartLinesUpdateEvent(items);

      try {
        const response = await this.postItems(items, sections);
        this.verifyAddedItems(response, items);
        await this.completeSuccessfulAdd(response, items, sections, cart, linesDeferred);
      } catch (error) {
        const reconciledCart = await this.fetchCart(sections).catch(() => null);
        const canConfirmSuccess =
          error?.allowReconcileSuccess === true &&
          beforeCart &&
          reconciledCart &&
          this.didAllItemsIncrease(beforeCart, reconciledCart, items);

        if (canConfirmSuccess) {
          await this.completeSuccessfulAdd(reconciledCart, items, sections, cart, linesDeferred);
        } else {
          if (reconciledCart && beforeCart && this.didAnyItemIncrease(beforeCart, reconciledCart, items)) {
            await this.refreshCartRenderer(reconciledCart, items, sections, cart).catch(() => null);
          }
          this.rejectCartLinesUpdateEvent(linesDeferred, error);
          this.reportCartError(error, items);
        }
      } finally {
        this.setLoading(false);
        this.renderSummary();
      }
    }

    async postItems(items, sections) {
      const root = window.Shopify?.routes?.root;
      if (!root) throw this.createError('No fue posible determinar la ruta del carrito.', false);

      const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : { method: 'POST', headers: {} };
      config.headers = {
        ...config.headers,
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      };
      config.body = JSON.stringify({
        items,
        sections,
        sections_url: window.location.pathname,
      });

      let fetchResponse;
      try {
        fetchResponse = await fetch(`${root}cart/add.js`, config);
      } catch (error) {
        throw this.createError(error.message || 'No fue posible conectar con el carrito.', true);
      }

      const response = await fetchResponse.json().catch(() => null);
      if (!fetchResponse.ok || response?.status) {
        const message = response?.description || response?.message || 'No fue posible agregar el look al carrito.';
        const allowReconcileSuccess = fetchResponse.status === 400;
        throw this.createError(message, allowReconcileSuccess, fetchResponse.status);
      }

      return response;
    }

    verifyAddedItems(response, requestedItems) {
      if (!Array.isArray(response?.items)) {
        throw this.createError('Shopify no confirmó las piezas agregadas.', true);
      }

      const returnedIds = new Set(response.items.map((item) => String(item.variant_id || item.id || '')));
      const allReturned = requestedItems.every((item) => returnedIds.has(String(item.id)));
      if (!allReturned) throw this.createError('Shopify no confirmó todas las piezas del look.', true);
    }

    async completeSuccessfulAdd(response, items, sections, cart, linesDeferred) {
      this.resolveCartLinesUpdateEvent(linesDeferred);

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        try {
          await publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'aa-shop-the-look',
            productVariantId: items[0]?.id,
            productVariantIds: items.map((item) => item.id),
            cartData: response,
          });
        } catch (_error) {
          // Rendering below remains the source of truth for the visible cart.
        }
      }

      try {
        await this.refreshCartRenderer(response, items, sections, cart);
        this.successElement.textContent = this.successCopy;
      } catch (_error) {
        const refreshedCart = await this.fetchCart(sections).catch(() => null);
        if (refreshedCart) {
          try {
            await this.refreshCartRenderer(refreshedCart, items, sections, cart);
            this.successElement.textContent = this.successCopy;
          } catch (_refreshError) {
            this.showError('El look fue agregado, pero no fue posible actualizar visualmente el carrito.');
          }
        } else {
          this.showError('El look fue agregado, pero no fue posible actualizar visualmente el carrito.');
        }
      }
    }

    async refreshCartRenderer(response, items, sections, cart) {
      let renderState = response;

      if (!this.hasRenderableSections(response, sections)) {
        renderState = await this.fetchCart(sections);
      }

      if (cart?.renderContents) {
        if (cart.tagName === 'CART-NOTIFICATION') {
          const firstKey = this.findLineItemKey(renderState, items[0]?.id);
          if (!firstKey) throw new Error('Missing cart notification line key');
          cart.renderContents({ ...renderState, key: firstKey });
        } else {
          cart.renderContents(renderState);
          cart.classList.remove('is-empty');
        }
        return;
      }

      const iconHtml = renderState?.sections?.['cart-icon-bubble'];
      const iconMount = document.getElementById('cart-icon-bubble');
      if (!iconHtml || !iconMount) return;

      const source = new DOMParser().parseFromString(iconHtml, 'text/html').querySelector('.shopify-section');
      if (source) iconMount.innerHTML = source.innerHTML;
    }

    hasRenderableSections(response, sections) {
      if (!sections.length) return true;
      return sections.every((sectionId) => typeof response?.sections?.[sectionId] === 'string');
    }

    findLineItemKey(cartData, variantId) {
      return cartData?.items?.find(
        (item) => String(item.variant_id || item.id || '') === String(variantId || '')
      )?.key;
    }

    async fetchCart(sections = []) {
      const root = window.Shopify?.routes?.root;
      if (!root) throw new Error('Missing locale-aware cart root');

      const query = sections.length ? `?sections=${encodeURIComponent(sections.join(','))}` : '';
      const response = await fetch(`${root}cart.js${query}`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('Unable to reconcile cart');
      return response.json();
    }

    getVariantQuantities(cartData) {
      const quantities = new Map();
      (cartData?.items || []).forEach((item) => {
        const id = String(item.variant_id || item.id || '');
        quantities.set(id, (quantities.get(id) || 0) + Number(item.quantity || 0));
      });
      return quantities;
    }

    didAllItemsIncrease(beforeCart, afterCart, requestedItems) {
      const before = this.getVariantQuantities(beforeCart);
      const after = this.getVariantQuantities(afterCart);
      return requestedItems.every(
        (item) => (after.get(String(item.id)) || 0) >= (before.get(String(item.id)) || 0) + item.quantity
      );
    }

    didAnyItemIncrease(beforeCart, afterCart, requestedItems) {
      const before = this.getVariantQuantities(beforeCart);
      const after = this.getVariantQuantities(afterCart);
      return requestedItems.some(
        (item) => (after.get(String(item.id)) || 0) > (before.get(String(item.id)) || 0)
      );
    }

    createCartLinesUpdateEvent(items) {
      const CartLinesUpdateEvent = window.StandardEvents?.CartLinesUpdateEvent;
      if (!CartLinesUpdateEvent) return null;

      try {
        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: items.map((item) => ({ merchandiseId: item.id, quantity: item.quantity })),
            promise: deferred.promise,
          })
        );
        return deferred;
      } catch (_error) {
        return null;
      }
    }

    resolveCartLinesUpdateEvent(deferred) {
      if (!deferred) return;

      const CartLinesUpdateEvent = window.StandardEvents?.CartLinesUpdateEvent;
      if (!CartLinesUpdateEvent) return;

      const cartPromise =
        typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : this.fetchCart();

      cartPromise
        .then((cart) => {
          if (!cart?.currency) throw new Error('Missing currency in cart response');
          deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
        })
        .catch((error) => deferred.reject(error));
    }

    rejectCartLinesUpdateEvent(deferred, error) {
      deferred?.reject(error instanceof Error ? error : new Error(String(error)));
    }

    reportCartError(error, items) {
      const message = error?.message || 'No fue posible agregar el look al carrito.';
      this.showError(message);

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'aa-shop-the-look',
          productVariantId: items[0]?.id,
          productVariantIds: items.map((item) => item.id),
          errors: message,
          message,
        });
      }

      const CartErrorEvent = window.StandardEvents?.CartErrorEvent;
      if (CartErrorEvent) {
        try {
          this.dispatchEvent(
            new CartErrorEvent({
              error: message,
              code: error?.status === 422 ? 'INVALID' : 'SERVICE_UNAVAILABLE',
            })
          );
        } catch (_error) {
          // The inline role=alert remains available even without Standard Events.
        }
      }
    }

    createError(message, allowReconcileSuccess = false, status = null) {
      const error = new Error(message);
      error.allowReconcileSuccess = allowReconcileSuccess;
      error.status = status;
      return error;
    }

    showError(message) {
      this.errorElement.textContent = message;
      this.errorElement.hidden = false;
      this.successElement.textContent = '';
    }

    clearMessages() {
      if (this.errorElement) {
        this.errorElement.textContent = '';
        this.errorElement.hidden = true;
      }
      if (this.successElement) this.successElement.textContent = '';
    }

    get addLabel() {
      return this.dataset.aaAddLabel || 'Agregar look al carrito';
    }

    get loadingLabel() {
      return this.dataset.aaLoadingLabel || 'Cargando';
    }

    get pendingCopy() {
      return this.dataset.aaPendingCopy || 'Selecciona las opciones pendientes para continuar.';
    }

    get emptyCopy() {
      return this.dataset.aaEmptyCopy || 'Selecciona al menos una pieza.';
    }

    get mainPendingCopy() {
      return this.dataset.aaMainPendingCopy || this.pendingCopy;
    }

    get soldOutCopy() {
      return this.dataset.aaSoldOutCopy || 'Agotado';
    }

    get nonexistentCopy() {
      return this.dataset.aaNonexistentCopy || 'Combinación inexistente';
    }

    get excludedCopy() {
      return this.dataset.aaExcludedCopy || 'No incluida';
    }

    get readyCopy() {
      return this.dataset.aaReadyCopy || 'Lista para agregar';
    }

    get successCopy() {
      return this.dataset.aaSuccessCopy || 'Look agregado al carrito.';
    }
  }

  customElements.define(ELEMENT_NAME, AaShopTheLook);
})();
